import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Eye, EyeOff, Lock, LogIn, Mail } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { signIn, requestPasswordReset } from "@/integrations/api/auth";
import { storeTenantHost } from "@/integrations/api/client";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: z.object({ next: z.string().optional() }),
  component: AuthPage,
});

function AuthPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"signin" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      if (mode === "signin") {
        const host = window.location.host;
        if (host.split(":")[0].includes(".")) storeTenantHost(host);
        await signIn(email, password);
        const { getMe } = await import("@/integrations/api/auth");
        const { useAuthStore } = await import("@/store/useAuthStore");
        const me = await getMe();
        queryClient.setQueryData(currentUserQueryOptions().queryKey, me);
        useAuthStore.getState().setAuth(me);

        if (me.must_change_password) {
          navigate({ to: "/change-password" });
        } else if (next && next.startsWith("/")) {
          window.location.assign(next);
        } else {
          const { homePathForRoles } = await import("@/lib/roles");
          navigate({ to: homePathForRoles(me.roles) });
        }
      } else {
        await requestPasswordReset(email);
        setMsg("If an account exists, a password reset link has been sent.");
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const isForgot = mode === "forgot";

  return (
    <div className="grid h-dvh place-items-center overflow-hidden bg-surface-soft px-4">
      <form
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-sm"
        onSubmit={submit}
      >
        <div className="mb-5 flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            H
          </span>
          <div>
            <p className="text-sm font-semibold leading-none text-foreground">HAMS</p>
            <p className="mt-1 text-xs text-muted-foreground">Hospital Asset Management</p>
          </div>
        </div>

        <label className="block text-xs font-medium text-foreground" htmlFor="email">
          Email
        </label>
        <div className="relative mt-1.5">
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            placeholder="name@hospital.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {!isForgot && (
          <>
            <div className="mt-3 flex items-center justify-between">
              <label className="text-xs font-medium text-foreground" htmlFor="password">
                Password
              </label>
              <button
                type="button"
                onClick={() => { setMode("forgot"); setErr(null); setMsg(null); }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative mt-1.5">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                minLength={6}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-10 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </>
        )}

        {err && (
          <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </p>
        )}
        {msg && (
          <p className="mt-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground">
            {msg}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          <LogIn className="h-4 w-4" />
          {busy ? "Please wait…" : isForgot ? "Send reset link" : "Sign in"}
        </button>

        {isForgot && (
          <button
            type="button"
            onClick={() => { setMode("signin"); setErr(null); setMsg(null); }}
            className="mt-3 w-full text-center text-sm font-medium text-primary hover:underline"
          >
            Back to sign in
          </button>
        )}
      </form>
    </div>
  );
}
